import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
  memo,
} from 'react';
import InterfaceSelect from 'components/select';
import {
  Box,
  useColorModeValue,
  useToast,
  Button,
  Input,
  Flex,
  FormControl,
  FormLabel,
  Spinner,
  VStack,
  Center,
  Badge,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  HStack,
  Text,
  Heading,
} from '@chakra-ui/react';
import Card from 'components/card/Card';
import axiosInstance from 'api/axiosInstance';
import apiUrls from 'api/apiUrls';
import ComplexTable from '../default/components/ComplexTable';
import { ChevronDownIcon } from '@chakra-ui/icons';
import { sortDevices } from 'views/hooks/sortDevices';

const INTERVAL_OPTIONS = {
  '5s': 5000,
  '10s': 10000,
  '30s': 30000,
  '1m': 60000,
  '5m': 300000,
};

const TIME_RANGE_OPTIONS = {
  '1m': 1 * 60 * 1000,
  '5m': 5 * 60 * 1000,
  '15m': 15 * 60 * 1000,
  '30m': 30 * 60 * 1000,
  '1h': 60 * 60 * 1000,
  '6h': 6 * 60 * 60 * 1000,
  '12h': 12 * 60 * 60 * 1000,
  '1d': 24 * 60 * 60 * 1000,
  '2d': 2 * 24 * 60 * 60 * 1000,
  '3d': 3 * 24 * 60 * 60 * 1000,
  '4d': 4 * 24 * 60 * 60 * 1000,
  '5d': 5 * 24 * 60 * 60 * 1000,
  '6d': 6 * 24 * 60 * 60 * 1000,
  '7d': 7 * 24 * 60 * 60 * 1000,
  custom: 'custom',
};

const LEVEL_OPTIONS = ['ERROR', 'WARNING', 'INFO', 'DEBUG'];

const levelColors = {
  INFO: 'blue',
  WARNING: 'yellow',
  ERROR: 'red',
  DEBUG: 'gray',
};

const API_URL = process.env.REACT_APP_WARROM_URL;

const DeviceSelect = memo(
  ({
    value,
    onChange,
    isDisabled,
    deviceOptions,
    loadingDevices,
    hasMoreDevices,
    loadMoreRef,
    onSearchChange,
    onShowMore,
  }) => {
    const inputBg = useColorModeValue('white', 'navy.600');
    const textColor = useColorModeValue('navy.700', 'white');
    const hoverBg = useColorModeValue('gray.200', 'navy.500');
    const focusBorderColor = useColorModeValue('navy.500', 'navy.300');
    const borderColor = useColorModeValue('gray.300', 'navy.600');
    const [isOpen, setIsOpen] = useState(false);
    const [searchValue, setSearchValue] = useState('');
    const searchTimeoutRef = useRef(null);

    const handleSearchChange = (e) => {
      const value = e.target.value;
      setSearchValue(value);

      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }

      searchTimeoutRef.current = setTimeout(() => {
        if (onSearchChange) {
          onSearchChange(value);
        }
      }, 500);
    };

    useEffect(() => {
      return () => {
        if (searchTimeoutRef.current) {
          clearTimeout(searchTimeoutRef.current);
        }
      };
    }, []);

    return (
      <Box position="relative">
        <Menu isOpen={isOpen} onClose={() => setIsOpen(false)}>
          <MenuButton
            as={Button}
            rightIcon={<ChevronDownIcon />}
            bg={inputBg}
            color={textColor}
            borderRadius="lg"
            border="2px"
            borderColor={borderColor}
            _hover={{ bg: hoverBg }}
            _focus={{
              outline: 'none',
              borderColor: focusBorderColor,
              boxShadow: `0 0 0 1px ${focusBorderColor}`,
            }}
            size="sm"
            width="100%"
            textAlign="left"
            isDisabled={isDisabled}
            onClick={() => setIsOpen(!isOpen)}
          >
            {value || 'Select Device'}
          </MenuButton>
          <MenuList
            borderRadius="lg"
            fontSize="14px"
            p="2"
            bg={inputBg}
            color={textColor}
            maxH="300px"
            zIndex={100}
            overflowY="auto"
            boxShadow={
              'rgba(17, 17, 26, 0.1) 0px 8px 24px, rgba(17, 17, 26, 0.1) 0px 16px 56px, rgba(17, 17, 26, 0.1) 0px 24px 80px;'
            }
          >
            <Box p={2}>
              <Input
                placeholder="Search..."
                value={searchValue}
                onChange={handleSearchChange}
                size="sm"
                borderRadius="16px"
                bg={inputBg}
                color={textColor}
                borderColor={borderColor}
                _focus={{
                  borderColor: focusBorderColor,
                  boxShadow: `0 0 0 1px ${focusBorderColor}`,
                }}
              />
            </Box>

            {Object.entries(deviceOptions).map(([label]) => (
              <MenuItem
                key={label}
                bg={inputBg}
                onClick={() => {
                  onChange(label);
                  setIsOpen(false);
                }}
                _hover={{ bg: hoverBg }}
                fontWeight={500}
                my={2}
              >
                {label}
              </MenuItem>
            ))}

            {loadingDevices && (
              <Center p={2}>
                <Spinner size="sm" />
              </Center>
            )}

            {hasMoreDevices && !loadingDevices && (
              <Center p={2}>
                <Button
                  size="sm"
                  onClick={() => {
                    if (onShowMore) onShowMore();
                  }}
                  width="100%"
                  variant="outline"
                >
                  Show More
                </Button>
              </Center>
            )}
          </MenuList>
        </Menu>
      </Box>
    );
  },
);

DeviceSelect.displayName = 'DeviceSelect';

// Memoized LogsTable component to prevent unnecessary re-renders
// Memoized LogsTable component to prevent unnecessary re-renders
const LogsTable = memo(
  ({
    logs,
    levelFilter,
    loadingLogs,
    logsHasMore,
    setLogsPage,
    logsPage,
    fetchLogs,
    isFetchingLogs,
  }) => {
    const LOG_COLUMNS = useMemo(
      () => [
        {
          header: 'Timestamp',
          accessorKey: 'timestamp',
          cell: (info) => info.getValue(),
        },
        {
          header: 'Level',
          accessorKey: 'level',
          cell: (info) => (
            <Badge colorScheme={levelColors[info.row.original.level] || 'gray'}>
              {info.row.original.level}
            </Badge>
          ),
        },
        {
          header: 'Device ID',
          accessorKey: 'device_id',
          cell: (info) => info.getValue(),
        },
        {
          header: 'Service',
          accessorKey: 'service_name',
          cell: (info) => info.getValue(),
        },
        {
          header: 'Message',
          accessorKey: 'message',
          cell: (info) => info.getValue(),
        },
      ],
      [],
    );

    const filteredLogs = useMemo(() => {
      // console.log('Logs to display:', logs); // Debug log

      if (!logs || !Array.isArray(logs)) {
        // console.log('Logs is empty or not an array');
        return [];
      }

      if (levelFilter === 'all') return logs;

      return logs.filter((log) => {
        const level = log.level || '';
        return level.toUpperCase() === levelFilter.toUpperCase();
      });
    }, [logs, levelFilter]);

    // console.log('Filtered logs:', filteredLogs); // Debug log

    return (
      <ComplexTable
        columnsData={LOG_COLUMNS}
        tableData={filteredLogs}
        loading={loadingLogs}
        pageSize={20}
        enableLocalPagination={true}
        currentPage={logsPage}
        setCurrentPage={setLogsPage}
        showFirstLastButtons={true}
        // hasNext={logsHasMore}
        // fetchAllDevices={(search, _, page) => {
        //   console.log('Fetching more logs, page:', page);
        //   fetchLogs(page, true);
        // }}
        // totalPages={undefined}
        title="Device Logs"
      />
    );
  },
);

LogsTable.displayName = 'LogsTable';

LogsTable.displayName = 'LogsTable';

const Filters = memo(
  ({
    deviceFilter,
    setDeviceFilter,
    setLogsPage,
    loadingDevices,
    allDeviceNames,
    hasMoreDevices,
    deviceListRef,
    handleDeviceSearch,
    handleShowMoreDevices,
    timeRange,
    setTimeRange,
    levelFilter,
    setLevelFilter,
    intervalTime,
    setIntervalTime,
    customRange,
    setCustomRange,
  }) => {
    return (
      <Flex
        direction="row"
        gap={4}
        wrap="nowrap"
        justify="space-between"
        align="center"
      >
        {/* Device */}
        <FormControl width="200px">
          <FormLabel fontSize="sm">Device</FormLabel>
          <DeviceSelect
            value={deviceFilter}
            onChange={(val) => {
              setDeviceFilter(val);
              setLogsPage(1);
            }}
            isDisabled={loadingDevices}
            deviceOptions={allDeviceNames}
            loadingDevices={loadingDevices}
            hasMoreDevices={hasMoreDevices}
            loadMoreRef={deviceListRef}
            onSearchChange={handleDeviceSearch}
            onShowMore={handleShowMoreDevices}
          />
        </FormControl>

        {/* Time Range */}
        <FormControl width="150px">
          <FormLabel fontSize="sm">Time Range</FormLabel>
          <InterfaceSelect
            value={timeRange}
            onChange={(val) => {
              setTimeRange(val);
              setLogsPage(1);
            }}
            options={TIME_RANGE_OPTIONS}
          />
        </FormControl>

        {/* Custom Date Range */}
        {timeRange === 'custom' && (
          <FormControl width="420px">
            <FormLabel fontSize="sm">Date Range</FormLabel>

            <HStack spacing={2}>
              <Input
                type="datetime-local"
                size="sm"
                value={customRange.start}
                bg="white"
                borderRadius="md"
                onChange={(e) =>
                  setCustomRange((prev) => ({
                    ...prev,
                    start: e.target.value,
                  }))
                }
              />

              <Text fontSize="sm" color="gray.500">
                to
              </Text>

              <Input
                type="datetime-local"
                size="sm"
                value={customRange.end}
                bg="white"
                borderRadius="md"
                onChange={(e) =>
                  setCustomRange((prev) => ({
                    ...prev,
                    end: e.target.value,
                  }))
                }
              />
            </HStack>
          </FormControl>
        )}

        {/* Log Level */}
        <FormControl width="150px">
          <FormLabel fontSize="sm">Log Level</FormLabel>
          <InterfaceSelect
            value={levelFilter}
            onChange={(val) => {
              setLevelFilter(val);
              setLogsPage(1);
            }}
            options={{
              All: 'all',
              ...Object.fromEntries(LEVEL_OPTIONS.map((l) => [l, l])),
            }}
          />
        </FormControl>

        {/* Refresh Interval */}
        <FormControl width="150px">
          <FormLabel fontSize="sm">Refresh Interval</FormLabel>
          <InterfaceSelect
            value={intervalTime}
            onChange={(val) => setIntervalTime(Number(val))}
            options={INTERVAL_OPTIONS}
          />
        </FormControl>
      </Flex>
    );
  },
);

Filters.displayName = 'Filters';

const Logs = () => {
  const toast = useToast();
  const [logs, setLogs] = useState([]);
  const [intervalTime, setIntervalTime] = useState(60000);
  const [timeRange, setTimeRange] = useState(5 * 60 * 1000);
  const [customRange, setCustomRange] = useState({ start: '', end: '' });

  const [deviceFilter, setDeviceFilter] = useState('');
  const [levelFilter, setLevelFilter] = useState('all');
  const [allDeviceNames, setAllDeviceNames] = useState({});
  const [loadingDevices, setLoadingDevices] = useState(false);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [expandedRow, setExpandedRow] = useState(null);
  const [hasMoreDevices, setHasMoreDevices] = useState(true);
  const [devicePage, setDevicePage] = useState(0);
  const [deviceSearch, setDeviceSearch] = useState('');
  const [logsHasMore, setLogsHasMore] = useState(false);
  const [logsPage, setLogsPage] = useState(1);
  const [isFetchingLogs, setIsFetchingLogs] = useState(false);
  const deviceName = localStorage.getItem('deviceId');
  const intervalRef = useRef(null);
  const tableRef = useRef(null);
  const deviceListRef = useRef(null);
  const observerRef = useRef(null);

  const [allLogs, setAllLogs] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  // const pageSize = 20;

  // Fetch devices with infinite scroll
  const fetchAllDevices = useCallback(
    async (page = 0, search = '', { ensureDevice } = {}) => {
      setLoadingDevices(true);

      try {
        const resp = await axiosInstance.get(apiUrls.allDevices(search, page));
        const devices = sortDevices((resp.data.data || []).map((d) => d.name));
        const hasNext = resp.data.hasNext || false;

        const deviceMap = devices.reduce((acc, name) => {
          acc[name] = name;
          return acc;
        }, {});

        if (page === 0) {
          setAllDeviceNames(deviceMap);

          // Only set deviceFilter if not already set and not ensuring a specific device
          if (devices.length > 0 && !deviceFilter && !ensureDevice) {
            setDeviceFilter(devices[0].name);
          }
        } else {
          setAllDeviceNames((prev) => ({ ...prev, ...deviceMap }));
        }

        setHasMoreDevices(hasNext);

        if (hasNext) {
          setDevicePage(page);
        }

        // If ensureDevice is provided, check if it's in the list, otherwise fetch it by search
        if (ensureDevice && !deviceMap[ensureDevice]) {
          // Search for the device by name
          const searchResp = await axiosInstance.get(
            apiUrls.allDevices(ensureDevice, 0),
          );
          const searchDevices = searchResp.data.data || [];
          const found = searchDevices.find((d) => d.name === ensureDevice);
          if (found) {
            setAllDeviceNames((prev) => ({
              ...prev,
              [found.name]: found.name,
            }));
            setDeviceFilter(found.name);
          }
        } else if (ensureDevice && deviceMap[ensureDevice]) {
          setDeviceFilter(ensureDevice);
        }
      } catch (err) {
        toast({
          title: 'Error fetching device list',
          description: err.message || 'Something went wrong.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setLoadingDevices(false);
      }
    },
    [deviceFilter, toast],
  );

  // Load more devices when "Show More" is clicked
  const handleShowMoreDevices = useCallback(() => {
    if (!loadingDevices && hasMoreDevices) {
      fetchAllDevices(devicePage + 1, deviceSearch);
    }
  }, [
    loadingDevices,
    hasMoreDevices,
    devicePage,
    deviceSearch,
    fetchAllDevices,
  ]);

  // Load more devices when scrolling
  const loadMoreDevices = useCallback(() => {
    if (!loadingDevices && hasMoreDevices) {
      fetchAllDevices(devicePage + 1, deviceSearch);
    }
  }, [
    loadingDevices,
    hasMoreDevices,
    devicePage,
    deviceSearch,
    fetchAllDevices,
  ]);

  // Add this useEffect to reset page when filters change
  useEffect(() => {
    setLogsPage(1);
  }, [deviceFilter, timeRange, levelFilter]);

  const fetchLogs = useCallback(async () => {
    if (!deviceFilter) {
      return;
    }

    // Prevent fetch if custom range is selected but not filled
    if (timeRange === 'custom' && (!customRange.start || !customRange.end)) {
      setAllLogs([]);
      setLogsHasMore(false);
      setLoadingLogs(false);
      return;
    }

    setLoadingLogs(true);

    try {
      let start, end;
      if (timeRange === 'custom') {
        start = new Date(customRange.start).toISOString();
        end = new Date(customRange.end).toISOString();
      } else {
        const now = new Date();
        start = new Date(now - timeRange).toISOString();
        end = now.toISOString();
      }

      const url = new URL(`${API_URL}/f_api/device-logs`);
      url.searchParams.append('device_id', deviceFilter);
      url.searchParams.append('start', start);
      url.searchParams.append('end', end);

      const resp = await fetch(url.toString());

      if (!resp.ok) {
        throw new Error(`HTTP error! Status: ${resp.status}`);
      }

      const data = await resp.json();
      let results = data?.data?.result || [];
      let hasMore = data?.data?.hasNext || false;

      const transformedResults = results.map((log) => {
        const { stream, values } = log;
        const [tsRaw, msg] =
          Array.isArray(values) && values.length > 0 ? values[0] : [null, ''];
        const timestampRaw = tsRaw ? Number(tsRaw) : 0;
        return {
          timestamp: tsRaw
            ? new Date(timestampRaw / 1e6).toLocaleString('en-IN', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hour12: false,
              })
            : 'N/A',
          timestampRaw,
          level: (stream?.level || 'INFO').toUpperCase(),
          device_id: stream?.device_id || 'N/A',
          service_name: stream?.service_name || 'N/A',
          message: msg || 'No message',
        };
      });
      transformedResults.sort((a, b) => b.timestampRaw - a.timestampRaw);

      setLogsHasMore(hasMore);
      setAllLogs(transformedResults);
      setCurrentPage(1);
    } catch (err) {
      console.error('Error fetching logs:', err);

      toast({
        title: 'Error fetching logs',
        description: err.message || 'Something went wrong.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsFetchingLogs(false);
      setLoadingLogs(false);
    }
  }, [deviceFilter, timeRange, customRange, toast]);
  // Setup observer for device dropdown infinite scroll
  useEffect(() => {
    if (deviceListRef.current && hasMoreDevices && !loadingDevices) {
      const observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) {
            loadMoreDevices();
          }
        },
        { threshold: 0.5 },
      );

      observer.observe(deviceListRef.current);
      return () => observer.disconnect();
    }
  }, [hasMoreDevices, loadingDevices, loadMoreDevices]);

  // Initial load
  useEffect(() => {
    // Fetch first page, then ensure saved device is present if needed
    const savedDevice = localStorage.getItem('deviceId');
    if (savedDevice) {
      fetchAllDevices(0, '', { ensureDevice: savedDevice });
    } else {
      fetchAllDevices(0);
    }
  }, []);

  // Fetch logs when filters change - use a stable reference
  useEffect(() => {
    fetchLogs(1, false);
  }, [deviceFilter, timeRange, fetchLogs]);

  // Setup interval for refreshing logs
  useEffect(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);

    intervalRef.current = setInterval(() => {
      // Only fetch logs if we have a device selected
      if (deviceFilter) {
        fetchLogs(1, false);
      }
    }, intervalTime);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [intervalTime, fetchLogs, deviceFilter]);

  // Setup observer for logs table infinite scroll
  useEffect(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && logsHasMore && !isFetchingLogs) {
          fetchLogs(logsPage + 1, true);
        }
      },
      { threshold: 0.5 },
    );

    if (tableRef.current && logsHasMore && !isFetchingLogs) {
      observerRef.current.observe(tableRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [logsHasMore, isFetchingLogs, logsPage, fetchLogs]);

  const handleDeviceSearch = useCallback(
    (search) => {
      setDeviceSearch(search);
      fetchAllDevices(0, search);
    },
    [fetchAllDevices],
  );

  return (
    <Box>
      <Heading size="lg">Audit Logs</Heading>

      <Flex mt={5}>
        <Filters
          deviceFilter={deviceFilter}
          setDeviceFilter={setDeviceFilter}
          setLogsPage={setLogsPage}
          loadingDevices={loadingDevices}
          allDeviceNames={allDeviceNames}
          hasMoreDevices={hasMoreDevices}
          deviceListRef={deviceListRef}
          handleDeviceSearch={handleDeviceSearch}
          handleShowMoreDevices={handleShowMoreDevices}
          timeRange={timeRange}
          setTimeRange={setTimeRange}
          customRange={customRange}
          setCustomRange={setCustomRange}
          levelFilter={levelFilter}
          setLevelFilter={setLevelFilter}
          intervalTime={intervalTime}
          setIntervalTime={setIntervalTime}
        />
      </Flex>

      <Box mt={10} ref={tableRef}>
        <LogsTable
          logs={allLogs}
          levelFilter={levelFilter}
          loadingLogs={loadingLogs}
          logsHasMore={logsHasMore}
          logsPage={logsPage}
          fetchLogs={fetchLogs}
          isFetchingLogs={isFetchingLogs}
          setLogsPage={setLogsPage}
        />
      </Box>
    </Box>
  );
};

export default Logs;
